import * as colors from '../../00colors.js';
import { DeesInputBase } from '../dees-input-base/dees-input-base.js';
import { textInputContextMenuItems } from '../text-input-contextmenu.js';
import { demoFunc } from './dees-input-text.demo.js';

import {
  customElement,
  type TemplateResult,
  property,
  state,
  html,
  cssManager,
  css,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import '../../00group-overlay/dees-contextmenu/dees-contextmenu.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-input-text': DeesInputText;
  }
}

/** Native input types this field maps to. Anything richer has its own element. */
export type TTextInputType = 'text' | 'number' | 'date' | 'email' | 'url' | 'tel' | 'password';

/** Keyboard hint per type; `inputMode` on the host still wins. */
const defaultInputModes: Record<TTextInputType, string> = {
  text: 'text',
  number: 'decimal',
  date: 'text',
  email: 'email',
  url: 'url',
  tel: 'tel',
  password: 'text',
};

@customElement('dees-input-text')
export class DeesInputText extends DeesInputBase {
  public static demo = demoFunc;
  public static demoGroups = ['Input'];

  // INSTANCE
  /**
   * The native input type. `number` keeps the field's value numeric, so a form
   * collects a number rather than a numeric string; every other type keeps the
   * value a string. `password` masks the value; `isPasswordBool` additionally
   * renders the reveal button.
   */
  @property({
    type: String,
    reflect: true,
  })
  accessor type: TTextInputType = 'text';

  /** A string for every type except `number`, which holds a number or null while empty. */
  @property({
    type: String,
    reflect: true,
  })
  accessor value: string | number | null = '';

  @property({
    type: Boolean,
    reflect: true,
  })
  accessor isPasswordBool = false;

  @property({
    type: Boolean,
    reflect: true,
  })
  accessor showPasswordBool = false;

  @property({
    type: String,
    reflect: true,
  })
  accessor validationState!: 'valid' | 'warn' | 'invalid';

  @property({
    reflect: true,
  })
  accessor validationText: string = '';

  @property({ attribute: false })
  accessor validationFunction!: (value: string) => { valid: boolean; message?: string };

  @property({
    type: Boolean,
    reflect: true,
  })
  accessor frameless: boolean = false;

  @property({ attribute: false })
  accessor validConfirmed: boolean = false;

  /**
   * autofill hint forwarded to the inner input element.
   * Empty by default, which renders no `autocomplete` attribute at all — byte-identical to
   * this component before the property existed. Set it to a concrete token list to help
   * password managers (`'username'`, `'current-password'`, `'one-time-code'`) or to opt an
   * input into WebAuthn conditional mediation (`'username webauthn'`).
   */
  @property({ type: String })
  accessor autocomplete: string = '';

  @property({ type: String })
  accessor placeholder = '';

  private validTimeout: ReturnType<typeof setTimeout> | undefined;

  public static styles = [
    themeDefaultStyles,
    ...DeesInputBase.baseStyles,
    cssManager.defaultStyles,
    css`
      /* TODO: Migrate hardcoded values to --dees-* CSS variables */
      * {
        box-sizing: border-box;
      }

      :host {
        position: relative;
        z-index: auto;
        font-family: var(--dees-font-family);
      }

      .maincontainer {
        position: relative;
        color: var(--dees-color-text-primary);
      }

      input {
        display: flex;
        height: var(--dees-input-height);
        width: 100%;
        padding: 0 var(--dees-spacing-md);
        font-size: var(--dees-font-control-size);
        line-height: 1.5;
        text-align: var(--dees-input-control-align, left);
        border-radius: var(--dees-radius-lg);
        corner-shape: var(--dees-corner-shape);
        transition: background var(--dees-transition-fast);
        outline: none;
        cursor: text;
        font-family: inherit;
        color: var(--dees-color-text-primary);
      }

      input::placeholder {
        color: var(--dees-color-text-muted);
      }

      input:hover:not(:disabled):not(:focus) {
        border-color: var(--dees-color-border-strong);
      }

      input:focus {
        outline: 2px solid var(--dees-color-focus-ring);
        outline-offset: 1px;
        border-color: var(--dees-color-focus-ring);
      }

      input:disabled {
        background: var(--dees-color-badge-default-bg);
        border-color: var(--dees-color-border-default);
        color: var(--dees-color-text-muted);
        cursor: not-allowed;
        opacity: 0.5;
      }

      /* Password toggle button */
      .showPassword {
        position: absolute;
        right: 1px;
        top: calc(var(--dees-input-height) / 2);
        transform: translateY(-50%);
        display: flex;
        align-items: center;
        justify-content: center;
        width: calc(var(--dees-input-height) - 2px);
        height: calc(var(--dees-input-height) - 2px);
        cursor: pointer;
        color: var(--dees-color-text-muted);
        transition: all var(--dees-transition-fast) var(--dees-ease-standard);
        border-radius: 0 var(--dees-radius-md) var(--dees-radius-md) 0;
      }

      .showPassword:hover {
        background: var(--dees-color-badge-default-bg);
        color: var(--dees-color-text-primary);
      }

      .showPassword { border: none; background: transparent; font: inherit; }
      .showPassword:focus-visible { outline: 2px solid var(--dees-color-focus-ring); outline-offset: -3px; }
      .showPassword:disabled { cursor: default; }

      /* Valid checkmark icon */
      .validIcon {
        position: absolute;
        right: 10px;
        top: calc(var(--dees-input-height) / 2);
        transform: translateY(-50%);
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity var(--dees-transition-default) var(--dees-ease-standard);
        pointer-events: none;
        color: var(--dees-color-text-success);
      }

      .validIcon.show {
        opacity: 1;
      }

      .validIcon dees-icon {
        font-size: 18px;
      }

      /* Validation styles */
      /* Inline validation: plain accessible-color helper text
         below the field (no filled box). */
      .validationContainer {
        margin-top: var(--dees-spacing-xs);
        padding: 0;
        font-size: var(--dees-font-footnote-size);
        line-height: var(--dees-font-footnote-line);
        font-weight: 400;
        letter-spacing: var(--dees-font-footnote-tracking);
        transition: all var(--dees-transition-default) var(--dees-ease-standard);
        overflow: hidden;
      }

      .validationContainer.invalid {
        background: transparent;
        color: var(--dees-color-text-error);
      }

      .validationContainer.warn {
        background: transparent;
        color: var(--dees-color-text-warning);
      }

      .validationContainer.valid {
        background: transparent;
        color: var(--dees-color-text-success);
      }

      /* Error state for input */
      :host([validationstate="invalid"]) input {
        border-color: var(--dees-color-accent-error);
      }

      :host([validationstate="invalid"]) input:focus {
        border-color: var(--dees-color-accent-error);
        box-shadow: none;
      }

      /* Warning state for input */
      :host([validationstate="warn"]) input {
        border-color: var(--dees-color-accent-warning);
      }

      :host([validationstate="warn"]) input:focus {
        border-color: var(--dees-color-accent-warning);
        box-shadow: none;
      }

      /* Valid state for input */
      :host([validationstate="valid"]) input {
        border-color: var(--dees-color-text-success);
      }

      :host([validationstate="valid"]) input:focus {
        border-color: var(--dees-color-text-success);
        box-shadow: none;
      }

      /* Frameless mode: shed chrome to blend into a host component
         (e.g. a dees-table cell in edit mode). */
      :host([frameless]) dees-label,
      :host([frameless]) .validationContainer {
        display: none;
      }
      :host([frameless]) .maincontainer {
        height: var(--dees-control-height-lg);
      }
      :host([frameless]) input {
        height: var(--dees-control-height-lg);
        line-height: 24px;
        padding: 0 var(--dees-spacing-lg);
        font-size: var(--dees-font-control-size-sm);
        border: none;
        border-radius: 0;
        background: transparent;
        box-shadow: none;
        transition: none;
      }
      :host([frameless]) input:hover:not(:disabled):not(:focus),
      :host([frameless]) input:focus {
        border: none;
        outline: none;
        box-shadow: none;
        background: transparent;
      }
      :host([frameless]) .showPassword {
        display: none;
      }
      .field-messages { grid-column: 1 / -1; text-align: left; }
    `,
  ];

  public render(): TemplateResult {
    return html`
      <style>
        input {
          font-family: ${this.isPasswordBool ? 'var(--dees-font-family-mono)' : 'inherit'};
          letter-spacing: ${this.isPasswordBool ? '0.5px' : 'normal'};
          padding-right: ${this.isPasswordBool ? '48px' : this.validConfirmed ? '40px' : '12px'};
        }
        ${this.validationText && !this.validConfirmed
          ? css`
              .validationContainer {
                height: auto;
                opacity: 1;
                transform: translateY(0);
              }
            `
          : css`
              .validationContainer {
                height: 0;
                padding: 0 !important;
                opacity: 0;
                transform: translateY(-4px);
              }
            `}
      </style>
      <div class="input-wrapper">
        <dees-label .label=${this.label} .infoText=${this.infoText} .required=${this.required} @click=${() => this.focus()}></dees-label>
        <div class="maincontainer">
          <input
            class=${this.frameless ? '' : 'field-control'}
            type="${this.nativeInputType}"
            .value=${this.valueAsText}
            @input="${this.updateValue}"
            .disabled=${this.disabled}
            ?required=${this.required}
            aria-label=${this.label}
            aria-invalid=${this.validationState === 'invalid' ? 'true' : 'false'}
            aria-describedby="field-description field-validation"
            placeholder=${this.placeholder || (this.label ? '' : 'Enter text…')}
            inputmode=${this.inputMode || defaultInputModes[this.type] || 'text'}
          />
          ${this.isPasswordBool
            ? html`
                <button type="button" class="showPassword" @click=${this.togglePasswordView} ?disabled=${this.disabled}
                  aria-label=${this.showPasswordBool ? 'Hide password' : 'Show password'}>
                  <dees-icon .icon=${this.showPasswordBool ? 'lucide:Eye' : 'lucide:EyeOff'}></dees-icon>
                </button>
              `
            : html``}
          <div class="validIcon ${this.validConfirmed ? 'show' : ''}">
            <dees-icon .icon=${'lucide:Check'}></dees-icon>
          </div>
        </div>
        ${this.validationText || this.description ? html`<div class="field-messages">
          ${this.validationText
            ? html`
                <div id="field-validation" role="status" class="validationContainer ${this.validationState || 'invalid'}">
                  ${this.validationText}
                </div>
              `
            : html`<div id="field-validation" class="validationContainer"></div>`}
          <div id="field-description">${this.renderDescription()}</div>
        </div>` : ''}
      </div>
    `;
  }

  /** The type handed to the native input; masking wins over the declared type. */
  private get nativeInputType(): string {
    if (this.isPasswordBool || this.type === 'password') {
      return this.showPasswordBool ? 'text' : 'password';
    }
    return this.type;
  }

  /** The value as text, for validators, the context menu and the native element. */
  private get valueAsText(): string {
    return this.value === null || this.value === undefined ? '' : String(this.value);
  }

  /**
   * Keeps `value` in the shape the current type promises, whatever a host
   * binds: a number (or null while empty) for `number`, a string otherwise.
   */
  private normalizeValue(valueArg: string | number | null | undefined): string | number | null {
    if (this.type !== 'number') {
      return valueArg === null || valueArg === undefined ? '' : String(valueArg);
    }
    if (valueArg === null || valueArg === undefined || valueArg === '') return null;
    const parsed = typeof valueArg === 'number' ? valueArg : Number(valueArg);
    return Number.isNaN(parsed) ? null : parsed;
  }

  public willUpdate(changedProperties: Map<PropertyKey, unknown>): void {
    super.willUpdate(changedProperties);
    if (!changedProperties.has('value') && !changedProperties.has('type')) return;
    const normalized = this.normalizeValue(this.value);
    if (normalized !== this.value) this.value = normalized;
  }

  /**
   * `autocomplete` is applied imperatively rather than bound in the template so that the
   * default renders no attribute at all. Binding it would emit `autocomplete=""` on every
   * text input in the catalog, and an empty or `on` value is not universally equivalent to
   * an absent one across browser and password-manager heuristics.
   */
  public updated(_changedProperties: Map<string, any>): void {
    super.updated(_changedProperties);
    const input = this.shadowRoot?.querySelector('input');
    if (!input) {
      return;
    }
    if (this.autocomplete) {
      input.setAttribute('autocomplete', this.autocomplete);
    } else {
      input.removeAttribute('autocomplete');
    }
  }

  firstUpdated() {
    if (this.validationFunction && this.value !== null && this.value !== '') {
      const result = this.validationFunction(this.valueAsText);
      this.validationState = result.valid ? 'valid' : 'invalid';
      this.validationText = result.message || '';
      if (result.valid) {
        this.validConfirmed = false;
        this.validTimeout = setTimeout(() => {
          this.validConfirmed = true;
          this.validationState = undefined as any;
        }, 500);
      }
    }
  }

  public async updateValue(eventArg: Event) {
    const target = eventArg.target as HTMLInputElement;
    this.value = this.normalizeValue(target.value);
    if (this.validationFunction) {
      const result = this.validationFunction(this.valueAsText);
      this.validationState = result.valid ? 'valid' : 'invalid';
      this.validationText = result.message || '';
      if (result.valid) {
        this.validConfirmed = false;
        clearTimeout(this.validTimeout);
        this.validTimeout = setTimeout(() => {
          this.validConfirmed = true;
          this.validationState = undefined as any;
        }, 500);
      } else {
        clearTimeout(this.validTimeout);
        this.validConfirmed = false;
      }
    }
    this.changeSubject.next(this);
  }

  public getContextMenuItems() {
    return textInputContextMenuItems({
      input: this.shadowRoot!.querySelector('input')!, disabled: this.disabled,
      commit: value => { this.value = this.normalizeValue(value); this.changeSubject.next(this); },
    });
  }

  public getValue(): string | number | null {
    return this.value;
  }

  public setValue(value: string | number | null): void {
    this.value = this.normalizeValue(value);
  }

  public async togglePasswordView() {
    this.showPasswordBool = !this.showPasswordBool;
  }

  public async focus() {
    const textInput = this.shadowRoot!.querySelector('input');
    textInput!.focus();
  }

  public async blur() {
    const textInput = this.shadowRoot!.querySelector('input');
    textInput!.blur();
  }
}
